import { Analytics, Card, Language, Service } from '@yleisradio/areena-types';
import { useRef, useState } from 'react';
import { useUILanguage } from 'hooks/useUILanguage';
import { getSearchResults } from 'services/areena-api/fetchers';
import { useOnReceiveAnalytics } from 'hooks/analytics';

const FIRST_ELEMENT = 0;
const MAX_RESULTS_PER_COLUMN = 7;

const KeyCodes = {
  ESC: 27,
  ENTER: 13,
  UP: 38,
  DOWN: 40,
};

export interface SearchResult {
  service: Service;
  results: Card[];
}

const mapResultsToServiceResultObject = (
  service: Service,
  results: Card[]
): SearchResult => ({
  service,
  results,
});

const getServiceResults = (
  language: Language,
  service: Service,
  query: string,
  trackOnReceive: (analytics: Analytics | undefined) => void
) =>
  getSearchResults(language, query, service).then((results) => {
    if (results && results.data !== undefined) {
      if (typeof window !== 'undefined') {
        window.setTimeout(() => {
          trackOnReceive(results.meta?.analytics);
        }, 10);
      }
      return mapResultsToServiceResultObject(service, results.data);
    }
  });

const search = (
  language: Language,
  service: Service | undefined = 'tv',
  query: string,
  trackOnReceive: (analytics: Analytics | undefined) => void
) =>
  Promise.all([
    getServiceResults(language, service, query, trackOnReceive),
    getServiceResults(
      language,
      service === 'tv' ? 'radio' : 'tv',
      query,
      trackOnReceive
    ),
  ]);

export const useCustomSearch = (service: Service | undefined) => {
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  const trackOnReceive = useOnReceiveAnalytics();

  const cursorRef = useRef<number>(0);
  const maxCursorRef = useRef<number>(0);
  const queryRef = useRef<string>('');
  const [, updateCursor] = useState<number>(0);
  const language = useUILanguage();

  const getNthSearchResult = (index: number) => {
    return document.querySelectorAll('#search-results')[index + 1];
  };

  const onKeyEvent = (event: KeyboardEvent) => {
    switch (event.which) {
      case KeyCodes.ENTER:
        if (cursorRef.current >= 0) {
          const element = getNthSearchResult(cursorRef.current) as HTMLElement;
          if (element) {
            event.preventDefault();
            element.click();
            resetState();
          }
        }
        break;
      case KeyCodes.DOWN:
        moveCursor(cursorRef.current + 1);
        break;
      case KeyCodes.UP:
        moveCursor(cursorRef.current - 1);
        break;
      case KeyCodes.ESC:
        resetState();
        break;
      default:
    }
  };

  const moveCursor = (position: number) => {
    const max = maxCursorRef.current;

    if (position < FIRST_ELEMENT) {
      cursorRef.current = max - 1;
      updateCursor(cursorRef.current + 1);
    } else if (position > max - 1) {
      cursorRef.current = FIRST_ELEMENT;
      updateCursor(cursorRef.current + 1);
    } else {
      cursorRef.current = position;
      updateCursor(cursorRef.current + 1);
    }
  };

  const onInputChange = (event: KeyboardEvent) => {
    const queryInput = event.target as HTMLInputElement;

    if (event.which === KeyCodes.ESC || queryInput.value === '') {
      resetState();
    } else if (event.which !== KeyCodes.DOWN && event.which !== KeyCodes.UP) {
      queryRef.current = queryInput.value;

      search(language, service, queryInput.value, trackOnReceive).then(
        (items) => {
          updateState(items, queryInput.value);
        }
      );
    }
  };

  const getMaxResultsForColumn = (resultLength: number) =>
    resultLength < MAX_RESULTS_PER_COLUMN
      ? resultLength
      : MAX_RESULTS_PER_COLUMN;

  const updateState = (
    results: (SearchResult | undefined)[],
    query: string
  ) => {
    const filtered: SearchResult[] = results.filter(
      (i): i is SearchResult => !!i
    );
    setSearchResults(filtered);
    const resultsTV = filtered[0]
      ? getMaxResultsForColumn(filtered[0].results.length)
      : 0;
    const resultsRadio = filtered[1]
      ? getMaxResultsForColumn(filtered[1].results.length)
      : 0;
    maxCursorRef.current = resultsTV + resultsRadio;

    if (query === '') {
      resetState();
    }
  };

  const resetQuery = () => {
    const inputElement = document.querySelectorAll(
      '.yle-header-search-input'
    )[0] as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
  };

  const resetState = () => {
    setSearchResults([]);
    resetQuery();
    cursorRef.current = 0;
    updateCursor(0);
  };

  const blurWithTimeout = () => {
    setTimeout(resetState, 200);
  };

  return {
    resultsContainerSelector: '#search-result-container',
    cursor: () => {
      return cursorRef.current;
    },
    query: () => {
      return queryRef.current;
    },
    searchResults: searchResults,
    eventListeners: {
      keyup: (e: KeyboardEvent) => {
        onInputChange(e);
      },
      keydown: (e: KeyboardEvent) => {
        onKeyEvent(e);
      },
      blur: () => {
        blurWithTimeout();
      },
    },
  };
};
